import React, {ReactNode} from "react";
import {Checkbox} from "primereact/checkbox";
import FileHelper from "../../shared/helper/FileHelper";
import PortalDocument, {PortalDocumentInterface} from "../document/PortalDocument";
import Preview from "./Preview";
import DocumentSettings from "../document/DocumentSettings";
import {Button} from "primereact/button";
import SidebarLeftMode from "../viewer/settings/SidebarLeftMode";
import SidebarRightMode from "../viewer/settings/SidebarRightMode";
import Downloader from "../document/Downloader";
import Parameter from "../dialog/parameter/Parameter";
import {PortalGlobals} from "../Portal";
import {PdfaDialogDefinition} from "../dialog/components/convert/PdfaDialog";
import FileSizeUnit from "../../shared/units/FileSizeUnit";
import PortalContext from "../PortalContext";
import {DeleteDocumentType} from "../../shared/models/DeleteDocumentType";
import Immutable from "../../shared/immutable/Immutable";
import {SortType} from "../document/DocumentSorting";
import PortalDocumentManager from "../document/PortalDocumentManager";
import Translate from "../../shared/translation/Translate";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import Icon from "../../shared/components/Icon";
import {DebugGlobals} from "../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {MessageType} from "../../shared/log/MessageInterface";
import MessageBox from "../../shared/components/MessageBox";
import Exception from "../../shared/exception/Exception";
import PortalPureComponent from "../../shared/components/PortalPureComponent";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";

interface DocumentPreviewItemProperties extends PortalGlobals, I18nGlobals, DebugGlobals {
	filtered: boolean;
	portalDocument: Immutable<PortalDocumentInterface>;
	sortBy: SortType;
}

interface DocumentPreviewItemStatus {
	numberOfPages: number;
}

export class DocumentPreviewItemDefinition extends PortalPureComponent<DocumentPreviewItemProperties,
	DocumentPreviewItemStatus> {

	constructor(props: Readonly<DocumentPreviewItemProperties>) {
		super(props);

		this.state = {
			numberOfPages: 0
		};

		this.viewPage = this.viewPage.bind(this);
		this.renderOptions = this.renderOptions.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.setNumberOfPages();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<DocumentPreviewItemProperties>, prevState: Readonly<DocumentPreviewItemStatus>
	): Promise<void> {
		await this.setNumberOfPages();
	}

	private async setNumberOfPages(): Promise<void> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let numberOfPages: number = await portalDocument.getNumberOfPages();

		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
			);

			if (FunctionHelper.exists(attachment)) {
				numberOfPages = await portalDocument.getNumberOfPages(attachment);
			}
		} else {
			numberOfPages = await portalDocument.getNumberOfPages();
		}

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	/**
	 * Show the given document and selected page in the main viewer
	 * @private
	 */
	private viewPage(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		this.props.context.documentManager.open(this.props.portalDocument.get().id!);
	}

	private renderOptions(): ReactNode {
		let context: PortalContext = this.props.context;
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let hasOptions: boolean = (portalDocument.isArchive() && PdfaDialogDefinition.isActive(
				[portalDocument], this.props.context.info, this.props.signatureEditProfile
			)) || portalDocument.hasSignature() || portalDocument.hasPasswordProtection() ||
			portalDocument.hasCertificateProtection() || portalDocument.getAttachments().length > 0;

		if (!hasOptions) {
			return null;
		}

		return (
			<>
				{portalDocument.isArchive() && !portalDocument.isPortfolio() &&
					<Button icon="icon icon-archive small"
							className={this.props.sortBy === SortType.ARCHIVE ? " sorted" : ""}
							tooltip={Translate.value(
								"startscreen.panels.documentmanager.archive", {ns: "screen"}
							)}
							onClick={(event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection
								context.documentManager.open(this.props.portalDocument.get()?.id!);

								if (PdfaDialogDefinition.isActive(
									[portalDocument], this.props.context.info,
									this.props.signatureEditProfile
								)) {
									this.props.context.getPage().setState({
										showPdfaDialog: true,
										pdfaParameter: undefined
									});
								}
							}}>
						{this.props.portalDocument.get().archiveType}
					</Button>
				}
				{portalDocument.hasSignature() && !portalDocument.isPortfolio() &&
					<Button icon="icon icon-signature small"
							className={this.props.sortBy === SortType.SIGNATURE ? " sorted" : ""}
							tooltip={Translate.value(
								"startscreen.panels.documentmanager.signature", {ns: "screen"}
							)}
							onClick={(event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection
								documentSettings.sidebarLeftMode = SidebarLeftMode.SIGNATURES;
								context.documentManager.open(this.props.portalDocument.get().id!);
							}}>
						{this.props.portalDocument.get().signatures}
					</Button>
				}
				{(portalDocument.hasPasswordProtection() || portalDocument.hasCertificateProtection()) &&
					<Button icon="icon icon-locked"
							className={this.props.sortBy === SortType.PASSWORD ? " sorted" : ""}
							tooltip={
								portalDocument.hasPasswordProtection() ?
									Translate.value(
										"startscreen.panels.documentmanager.columns.security.password",
										{ns: "screen"}
									) :
									portalDocument.hasCertificateProtection() ?
										Translate.value(
											"startscreen.panels.documentmanager.columns.security.certificate",
											{ns: "screen"}
										) :
										undefined
							}
							onClick={(event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection
								documentSettings.sidebarRightMode = SidebarRightMode.SECURITY;
								context.documentManager.open(this.props.portalDocument.get().id!);
							}}>
						{portalDocument.hasPasswordProtection() &&
							<Icon icon={"icon-password"}/>
						}
						{portalDocument.hasCertificateProtection() &&
							<Icon icon={"icon-certificate"}/>
						}
					</Button>
				}
				{portalDocument.getAttachments().length > 0 && !portalDocument.isPortfolio() &&
					<Button icon="icon icon-attachment small"
							className={this.props.sortBy === SortType.ATTACHMENT ? " sorted" : ""}
							tooltip={Translate.value(
								"startscreen.panels.documentmanager.attachment", {ns: "screen"}
							)}
							onClick={(event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection

								if (!portalDocument.isPortfolio()) {
									documentSettings.sidebarLeftMode = SidebarLeftMode.ATTACHMENTS;
								}

								context.documentManager.open(this.props.portalDocument.get().id!);
							}}>
						{portalDocument.getAttachments().length}
					</Button>
				}
			</>
		);
	}

	/**
	 * render component as react node
	 */
	render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let downloader: Downloader = new Downloader(this.props.context.session.getToken()!);
		let executionError: Exception | undefined = portalDocument.getExecutionError();

		return <>
			<div className={"list-item" + (documentSettings.selected ? " selected" : "")}
				 key={portalDocument.getSourceDocumentId()} onDoubleClick={this.viewPage}
				 data-id={portalDocument.getSourceDocumentId()} style={this.props.filtered ? {display: "none"} : {}}
				 onClick={(event) => {
					 if (event.ctrlKey || event.shiftKey) {
						 if (!documentSettings.selected) {
							 documentManager.select(this.props.portalDocument.get().id!, false);
						 } else {
							 documentManager.unselect(this.props.portalDocument.get().id!);
						 }
					 } else {
						 documentManager.select(this.props.portalDocument.get().id!, true);
					 }
				 }}>
				<div className="column-select"
					 onClick={(event) => {
						 event.stopPropagation();
					 }}>
					<Checkbox checked={documentSettings.selected}
							  onChange={() => {
								  if (documentSettings.selected) {
									  documentManager.unselect(this.props.portalDocument.get().id!);
								  } else {
									  documentManager.select(this.props.portalDocument.get().id!);
								  }
							  }}/>
				</div>
				<div className="column-preview">
					<Preview {...this.props} controls={true}/>
					{portalDocument.isArchive() && !documentSettings.editArchiveConfirmation &&
						<div className="preview-message">
							<Button
								label={Translate.value(
									"startscreen.panels.documentmanager.archive_editing", {ns: "screen"}
								)}
								onClick={(event) => {
									event.stopPropagation(); // stop document selection
									let portalDocument: PortalDocument =
										documentManager.findDocument(this.props.portalDocument.get().id!);
									let documentSettings: DocumentSettings = portalDocument.getSettings();
									documentSettings.editArchiveConfirmation = true;
									documentManager.updateGlobalDocumentList();
								}}/>
						</div>
					}
				</div>
				<div className="column-info">
					<div className={"name tooltip" + (this.props.sortBy === SortType.NAME ? " sorted" : "")}
						 data-pr-tooltip={
							 this.props.portalDocument.get().name +
							 (this.props.portalDocument.get().extension !== "" ?
								 "." + this.props.portalDocument.get().extension : "")
						 }>
						<span className="filename">{this.props.portalDocument.get().name}</span>
						<span className="fileextension">.{this.props.portalDocument.get().extension}</span>
					</div>
					<div className={"filesize tooltip" + (this.props.sortBy === SortType.SIZE ? " sorted" : "")}
						 data-pr-tooltip={
							 FunctionHelper.applyValue(
								 this.props.portalDocument.get().size, 0
							 ) + ' ' + FileSizeUnit.BYTES
						 }>
						{FileHelper.formatFileSize(FunctionHelper.applyValue(
							this.props.portalDocument.get().size, 0
						), 2)}
					</div>
				</div>
				<div className="column-options">
					{this.renderOptions()}
				</div>
				{FunctionHelper.exists(executionError) &&
					<div className="column-error">
						<Icon icon="icon-warning" autoHide={false} tooltipStyle={{maxWidth: "600px"}}
							  tooltip={
								  <MessageBox {...this.props} type={MessageType.DANGER}
											  stacktrace={
												  <>
													  <p>
														  {executionError?.message} (errorcode: {executionError?.code},
														  exitcode: {executionError?.exitCode})
													  </p>
													  <pre>{executionError?.stack}</pre>
												  </>
											  }>
									  {executionError?.message}
								  </MessageBox>
							  }/>
					</div>
				}
				<div className="column-functions">
					<Button className="button-delete" icon="icon icon-delete small"
							disabled={portalDocument.isLocked()}
							onClick={(event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection
								let parameter: Parameter<DeleteDocumentType> = new Parameter<DeleteDocumentType>(
									DeleteDocumentType.fromJson({
										documentIds: [this.props.portalDocument.get().id!]
									} as DeleteDocumentType)
								);

								this.props.context.getPage().setState({
									showDeleteDialog: true,
									deleteParameter: parameter
								});
							}}
							tooltip={Translate.value(
								"startscreen.panels.documentmanager.columns.functions.delete",
								{ns: "screen"}
							)}/>
					<Button className="button-download" icon="icon icon-download small"
							disabled={portalDocument.isLocked()}
							onClick={async (event: React.MouseEvent) => {
								event.stopPropagation(); // stop document selection

								await downloader.downloadDocument(
									this.props.portalDocument.get().id!, this.props.portalDocument.get().fileName
								);
							}}
							tooltip={Translate.value(
								"startscreen.panels.documentmanager.columns.functions.download",
								{ns: "screen"}
							)}/>
				</div>
				{this.state.numberOfPages > 0 &&
					<div className="column-pages">
						<Button tooltip={Translate.value(
							"startscreen.panels.documentmanager.pages", {ns: "screen"}
						)}
								onClick={(event: React.MouseEvent) => {
									event.stopPropagation(); // stop document selection
									documentSettings.sidebarLeftMode = SidebarLeftMode.PAGES;
									documentManager.open(this.props.portalDocument.get().id!);
								}}>
							{this.state.numberOfPages}
						</Button>
					</div>
				}
			</div>
		</>;
	}
}

const DocumentPreviewItem = React.memo(DocumentPreviewItemDefinition,
	(
		prevProps: Readonly<DocumentPreviewItemProperties>,
		nextProps: Readonly<DocumentPreviewItemProperties>
	) => {
		if (!prevProps.portalDocument.equals(nextProps.portalDocument)) {
			return false;
		}

		if (prevProps.sortBy !== nextProps.sortBy) {
			return false;
		}

		if (prevProps.filtered !== nextProps.filtered) {
			return false;
		}

		if (prevProps.signatureEditProfile !== nextProps.signatureEditProfile) {
			return false;
		}

		return true;
	}
);

export default DocumentPreviewItem;